/**
 * A class that manages calls to the Tasks API. Users make calls to the various
 * action methods, and this class will enqueue those actions and then, when
 * possible, make appropriate API calls to perform those actions.
 *
 * Originally this class batched multiple actions together. However, the Tasks
 * API stopped working with batches, and so this class was changed to send each
 * action individually (which increases latency, but oh well). However, the
 * design of the class is still heavily influenced by the original batching
 * process.
 *
 * If there are multiple calls for the same action on the same task, without
 * the former call being sent (either via an explicit Flush() call or because
 * the call just has not yet been performed) the earlier calls will be dropped
 * and the last one will be used.
 *
 * API calls are made asynchronously, not immediately when the action methods
 * are called. In order to handle the responses, each action method takes a
 * callback that will be called when that action is completed.
 */

/* eslint-disable */

import Vue from 'vue'

export default class Updater {
  /**
   * gapi: The gapi object from the Google Javascript library.
   * updateSavedCallback: A function that takes 1 bool, indicating whether all
   *   actions have been saved.
   * saveFailedCallback: A function that takes no arguments, indicating that an
   *   action failed to save.
   */
  constructor (gapi, updateSavedCallback, saveFailedCallback) {
    this._gapi = gapi

    // The list of Actions to perform.
    this._actions = []

    // True if an API call is being performed right now.
    this._callInProgress = false

    // Called whenever the updater needs to call an action or when there are no
    // more actions.
    this._updateSavedCallback = updateSavedCallback

    // Called whenever the updater failed to save an action.
    this._saveFailedCallback = saveFailedCallback
  }

  /**
   * Returns true if there are no actions to be performed.
   */
  isClean () {
    return this._actions.length === 0 && !this._callInProgress
  }

  /**
   * Flushes the queue soon.
   */
  flush () {
    this._updateSavedCallback(false)
    this._actions.push(new Flush())
    this._processActionsSoon()
  }

  /**
   * Makes sure that actions enqueued after this call are not sent until a
   * flush() call is made.
   */
  waitForFlush () {
    // By pushing a flush, we can ensure that when _performActions finds a
    // WaitForFlush in this._actions, it will be at the zeroth position.
    this._updateSavedCallback(false)
    this._actions.push(new Flush())
    this._actions.push(new WaitForFlush())
    this._processActionsSoon()
  }

  /**
   * Enqueues a new insert.
   *
   * taskListId: string of the ID of the task list
   * jsTask: JsTask object that is being inserted
   * previousJsTask: the previous JsTask, or null
   * callback: (response) -> !Promise, a function that is called with the
   *    response to the API call
   */
  newInsert (taskListId, jsTask, previousJsTask, callback) {
    this._updateSavedCallback(false)
    this._actions.push(new InsertAction(this._gapi, taskListId, jsTask, previousJsTask, callback))
    this._processActionsSoon()
  }

  /**
   * Enqueues a new delete.
   *
   * taskListId: string of the ID of the task list
   * jsTask: JsTask object that is being deleted
   * callback: (response) -> !Promise, a function that is called with the
   *    response to the API call
   */
  newDelete (taskListId, jsTask, callback) {
    this._updateSavedCallback(false)
    this._actions.push(new DeleteAction(this._gapi, taskListId, jsTask, callback))
    this._processActionsSoon()
  }

  /**
   * Enqueues a new update.
   *
   * taskListId: string of the ID of the task list
   * jsTask: JsTask object that is being updated
   * callback: (response) -> !Promise, a function that is called with the
   *    response to the API call
   */
  newUpdate (taskListId, jsTask, callback) {
    this._updateSavedCallback(false)
    this._actions.push(new UpdateAction(this._gapi, taskListId, jsTask, callback))
    this._processActionsSoon()
  }

  /**
   * Enqueues a new move.
   *
   * taskListId: string of the ID of the task list
   * jsTask: JsTask object that is being moved
   * previousJsTask: the previous JsTask, or null
   * callback: (response) -> !Promise, a function that is called with the
   *    response to the API call
   */
  newMove (taskListId, jsTask, previousJsTask, callback) {
    this._updateSavedCallback(false)
    this._actions.push(new MoveAction(this._gapi, taskListId, jsTask, previousJsTask, callback))
    this._processActionsSoon()
  }

  /**
   * Enqueues a new clear.
   *
   * taskListId: string of the ID of the task list
   * callback: (response) -> !Promise, a function that is called with the
   *    response to the API call
   */
  newClear (taskListId, callback) {
    this._updateSavedCallback(false)
    this._actions.push(new ClearAction(this._gapi, taskListId, callback))
    this._processActionsSoon()
  }

  /**
   * Enqueues setting the access token.
   *
   * accessToken: string of the access token
   */
  newSetAccessToken (accessToken) {
    this._updateSavedCallback(false)
    this._actions.push(new SetAccessToken(accessToken))
    this._processActionsSoon()
  }

  /**
   * Enqueues a new listing of the task lists.
   *
   * callback: (response) -> !Promise, a function that is called with the
   *    response to the API call
   */
  newListTaskLists (callback) {
    this._updateSavedCallback(false)
    this._actions.push(new ListTaskListsAction(this._gapi, callback))
    this._processActionsSoon()
  }

  /**
   * Enqueues a new listing of the tasks.
   *
   * taskListId: string of the ID of the task list
   * showHidden: bool whether to list hidden tasks
   * nextPageToken: null or string, opaque token to request the next page of
   *   results
   * callback: (response) -> !Promise, a function that is called with the
   *    response to the API call
   */
  newListTasks (taskListId, showHidden, nextPageToken, callback) {
    this._updateSavedCallback(false)
    this._actions.push(new ListTasksAction(this._gapi, taskListId, showHidden, nextPageToken, callback))
    this._processActionsSoon()
  }

  /**
   * Prepares to process actions on the next tick.
   */
  _processActionsSoon () {
    Vue.nextTick(function () { this._performActions() }.bind(this))
  }

  /**
   * Performs the enqueued actions, starting at the beginning of the _actions
   * list.
   *
   * If the first action is a Wait*, then it checks to see if there is a flush
   * or if enough time has passed, and only processes up to the flush (or all,
   * respectively) if that is the case.
   *
   * This method takes all the actions up until a Wait* or Flush action,
   * batches them, and sends each action individually. When the response
   * arrives, it calls the callbacks for each action there is a response for,
   * and then calls _processActionsSoon to continue processing the actions
   * list.
   */
  async _performActions () {
    if (!this._callInProgress && this._actions.length === 0) {
      this._updateSavedCallback(true)
    }
    if (this._callInProgress || this._actions.length === 0) {
      return
    }

    console.debug('Performing Updater actions')
    console.debug(this._actions)
    this._callInProgress = true

    // Remove leading flushes, if any, since they are useless as there are no
    // actions yet to send.
    while (this._actions.length > 0) {
      let action = this._actions.shift()
      if (!(action instanceof Flush)) {
        this._actions.unshift(action)
        break
      }
    }

    // Make sure the client is available before iterating through the actions
    // list.
    let client = await this._gapi.client()
    let inProgressActions = new Map()
    while (this._actions.length > 0) {
      let action = this._actions.shift()
      if (action instanceof SetAccessToken) {
        client.setToken({ 'access_token': action.accessToken })
        continue
      }
      if (action instanceof Flush) {
        // We leave the flush in the list, and remove it once the API call is
        // finished.
        this._actions.unshift(action)
        break
      }
      if (action instanceof WaitForFlush) {
        // Since waitForFlush() pushes a flush before the WaitForFlush, this
        // must be the first iteration of the while loop, and there are no in
        // progress actions. We need to check that a flush exists after this,
        // and only if it does should we proceed.
        let flushExists = false
        for (let tmpAction of this._actions) {
          if (tmpAction instanceof Flush) {
            flushExists = true
            break
          }
        }
        if (!flushExists) {
          if (inProgressActions.size > 0) {
            console.error('There were actions that should have been taken but will not be. This is an internal error.')
          }
          this._actions.unshift(action)
          this._callInProgress = false
          this._processActionsSoon()
          return
        }
      }
      if (action instanceof ApiAction) {
        // Deduplicate the actions that should be sent, and then later on call
        // them.
        inProgressActions.set(action.actionSignature(), action)
      }
    }

    if (inProgressActions.size > 0) {
      // Map iterates in insertion order, so this can ensure that the actions
      // are added in the same order as they were in _actions.
      for (let action of inProgressActions.values()) {
        let resp = await action.run()
        if (resp.status < 200 || resp.status >= 300) {
          this._saveFailedCallback()
        }
        action.getCallback()(resp)
      }
    }

    // Now that the actions have been completed, remove any leading flushes,
    // unlock and do another run soon, in case there are more actions to run.
    while (this._actions.length > 0) {
      if (this._actions[0] instanceof Flush) {
        this._actions.shift()
      } else {
        break
      }
    }
    this._callInProgress = false
    this._processActionsSoon()
  }
}

/**
 * An Action is something that the Updater can process.
 */
class Action {}

/**
 * An ApiAction is an abstraction of an API call to make.
 */
class ApiAction extends Action {
  /**
   * Runs the API request and returns the response.
   */
  async run() {
    throw new Error('Unimplemented')
  }

  /**
   * Returns the callback that should be invoked when this action is completed.
   */
  getCallback () {
    throw new Error('Unimplemented')
  }

  /**
   * Returns a string that represents the specific action. This is used to
   * determine whether two actions can be coalesced into one, and also an
   * identifier for determining which part of the response is for this action.
   * It will generally contain the name of the action and the task ID.
   */
  actionSignature () {
    throw new Error('Unimplemented')
  }
}

class InsertAction extends ApiAction {
  constructor (gapi, taskListId, jsTask, previousJsTask, callback) {
    super()
    this._gapi = gapi
    this._taskListId = taskListId
    this._jsTask = jsTask
    this._previousJsTask = previousJsTask
    this._callback = callback
    console.debug('Insert, id: ' + this._jsTask.id.toString() + ' previous: ' + (this._previousJsTask === null ? 'null' : this._previousJsTask.id))
  }

  async run () {
    let client = await this._gapi.client()
    return await client.tasks.tasks.insert(
      {
        'tasklist': this._taskListId,
        'previous': this._previousJsTask === null ? null : this._previousJsTask.gTask.id
      },
      {
        'title': this._jsTask.gTask.title
      })
  }

  getCallback () {
    return this._callback
  }

  actionSignature () {
    return 'insert-' + this._jsTask.id
  }
}

class DeleteAction extends ApiAction {
  constructor (gapi, taskListId, jsTask, callback) {
    super()
    this._gapi = gapi
    this._taskListId = taskListId
    this._jsTask = jsTask
    this._callback = callback
    console.debug('Delete, id: ' + this._jsTask.id.toString() + ' gid: ' + this._jsTask.gTask.id)
  }

  async run () {
    let client = await this._gapi.client()
    return await client.tasks.tasks.delete(
      {
        'tasklist': this._taskListId,
        'task': this._jsTask.gTask.id
      }, {})
  }

  getCallback () {
    return this._callback
  }

  actionSignature () {
    return 'delete-' + this._jsTask.id
  }
}

class UpdateAction extends ApiAction {
  constructor (gapi, taskListId, jsTask, callback) {
    super()
    this._gapi = gapi
    this._taskListId = taskListId
    this._jsTask = jsTask
    this._callback = callback
    console.debug('Update, id: ' + this._jsTask.id.toString() + ' gid: ' + this._jsTask.gTask.id + ' completed: ' + this._jsTask.gTask.status)
  }

  async run () {
    let client = await this._gapi.client()
    console.debug('adding id: ' + this._jsTask.id.toString() + ' completed: ' + this._jsTask.gTask.status)
    return await client.tasks.tasks.update(
      {
        'tasklist': this._taskListId,
        'task': this._jsTask.gTask.id
      },
      this._jsTask.gTask)
  }

  getCallback () {
    return this._callback
  }

  actionSignature () {
    return 'update-' + this._jsTask.id
  }
}

class MoveAction extends ApiAction {
  constructor (gapi, taskListId, jsTask, previousJsTask, callback) {
    super()
    this._gapi = gapi
    this._taskListId = taskListId
    this._jsTask = jsTask
    this._previousJsTask = previousJsTask
    this._callback = callback
    console.debug('Move, id: ' + this._jsTask.id.toString() + ' previous: ' + (this._previousJsTask === null ? 'null' : this._previousJsTask.id))
  }

  async run () {
    let client = await this._gapi.client()
    return await client.tasks.tasks.move(
      {
        'tasklist': this._taskListId,
        'task': this._jsTask.gTask.id,
        'previous': this._previousJsTask === null ? null : this._previousJsTask.gTask.id
      }, {})
  }

  getCallback () {
    return this._callback
  }

  actionSignature () {
    return 'move-' + this._jsTask.id
  }
}

class ClearAction extends ApiAction {
  constructor (gapi, taskListId, callback) {
    super()
    this._gapi = gapi
    this._taskListId = taskListId
    this._callback = callback
    console.debug('Clear, list id: ' + this._taskListId.toString())
  }

  async run () {
    let client = await this._gapi.client()
    return await client.tasks.tasks.clear(
      {
        'tasklist': this._taskListId
      }, {})
  }

  getCallback () {
    return this._callback
  }

  actionSignature () {
    return 'clear-' + this._taskListId
  }
}

class ListTaskListsAction extends ApiAction {
  constructor (gapi, callback) {
    super()
    this._gapi = gapi
    this._callback = callback
    console.debug('List task lists')
  }

  async run () {
    let client = await this._gapi.client()
    return await client.tasks.tasklists.list({}, {})
  }

  getCallback () {
    return this._callback
  }

  actionSignature () {
    return 'listtasklists'
  }
}

class ListTasksAction extends ApiAction {
  constructor (gapi, taskListId, showHidden, nextPageToken, callback) {
    super()
    this._gapi = gapi
    this._taskListId = taskListId
    this._showHidden = showHidden
    this._nextPageToken = nextPageToken
    this._callback = callback
    console.debug('List tasks, id: ' + this._taskListId.toString())
  }

  async run () {
    let client = await this._gapi.client()
    return await client.tasks.tasks.list({
      'tasklist': this._taskListId,
      'maxResults': 200,
      'showHidden': this._showHidden,
      'pageToken': this._nextPageToken
    }, {})
  }

  getCallback () {
    return this._callback
  }

  actionSignature () {
    return 'listtasks-' + this._taskListId
  }
}

/**
 * ControlActions are used to control how action processing is performed.
 */
class ControlAction extends Action {}

/**
 * A Flush acts like a break in a list of actions, ensuring that all the
 * actions enqueued prior to the flush are performed before any of the actions
 * afterwards.
 */
class Flush extends ControlAction {}

/**
 * A WaitForFlush makes sure that no API calls are made containing actions
 * enqueued after this action until a Flush action is enqueued, at which point
 * the actions between the WaitForFlush and the Flush are performed.
 */
class WaitForFlush extends ControlAction {}

/**
 * A SetAccessToken sets the client's access token that future API calls will
 * be made with.
 */
class SetAccessToken extends ControlAction {
  constructor (accessToken) {
    super()
    this.accessToken = accessToken
  }
}
